import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../../redux/store";
import { upsertShortstay, listenToShortstayData } from "../../../../redux/shortstaysSlice";
import { TextField, Box, Button } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

const T = {
  bg:       "#FFFFFF",
  text:     "#0A0A0A",
  textSoft: "#444444",
  muted:    "#9A9A9A",
  border:   "#EBEBEB",
  gold:     "#C9A84C",
  goldDark: "#9A7A2E",
  black:    "#0A0A0A",
  sans:     "'Montserrat', sans-serif",
};

const muiFieldSx = () => ({
  mb: 2,
  "& .MuiInputBase-input": {
    color: T.black,
    fontFamily: T.sans,
    fontSize: 13,
  },
  "& .MuiInputLabel-root": {
    color: T.textSoft,
    fontFamily: T.sans,
    fontSize: 12,
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: T.black,
  },
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#FAFAFA",
    borderRadius: "10px",
    boxShadow: "none",
    "& fieldset": { borderColor: T.border, borderWidth: 1 },
    "&:hover fieldset": { borderColor: T.black },
    "&.Mui-focused fieldset": { borderColor: T.black, borderWidth: 1 },
  },
});

const ShortTitle: React.FC = () => {
  const { listingId } = useParams<{ listingId: string }>();
  const dispatch = useDispatch<AppDispatch>();
  const currentListing = useSelector((state: RootState) => state.shortstays.currentListing);

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [neighborhood, setNeighborhood] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (listingId) dispatch(listenToShortstayData(listingId));
  }, [listingId, dispatch]);

  useEffect(() => {
    if (currentListing) {
      setTitle(currentListing.title || "");
      setDescription(currentListing.description || "");
      setNeighborhood(currentListing.neighborhood || "");
    }
  }, [currentListing]);

  const handleSave = async () => {
    if (!listingId) return;
    setIsSaving(true);
    setError(null);

    try {
      await dispatch(
        upsertShortstay({
          listingId,
          data: { title, description, neighborhood },
        })
      );
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      console.error(err);
      setError("Failed to save. Please try again.");
      setTimeout(() => setError(null), 5000);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Box sx={{ background: T.bg, color: T.text, fontFamily: T.sans, maxWidth: 560, mx: "auto", position: "relative" }}>

      {/* SAVING OVERLAY */}
      {isSaving && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            background: "rgba(255,255,255,0.65)",
            backdropFilter: "blur(6px)",
            zIndex: 1100,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: "20px",
          }}
        >
          <div
            style={{
              width: 56,
              height: 56,
              borderRadius: "50%",
              border: `3px solid ${T.border}`,
              borderTop: `3px solid ${T.black}`,
              animation: "spin 0.8s linear infinite",
            }}
          />
          <p style={{ fontSize: 12, fontWeight: 500, color: T.black, letterSpacing: "0.08em", textTransform: "uppercase", margin: 0 }}>
            Saving
          </p>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </Box>
      )}

      {/* SUCCESS TOAST */}
      {saved && (
        <Box
          sx={{
            position: "fixed",
            bottom: 32,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 1200,
            background: "#FFFFFF",
            border: "1px solid #D4EDDA",
            borderRadius: "12px",
            padding: "14px 22px",
            display: "flex",
            alignItems: "center",
            gap: "10px",
            boxShadow: "0 8px 30px rgba(0,0,0,0.12)",
            minWidth: 220,
          }}
        >
          <CheckCircleOutlineIcon sx={{ color: "#22883F" }} />
          <div>
            <p style={{ fontSize: 12, fontWeight: 600, color: "#22883F", margin: 0 }}>Successfully saved</p>
            <p style={{ fontSize: 11, color: T.muted, margin: "2px 0 0" }}>Listing details updated</p>
          </div>
        </Box>
      )}

      {/* ERROR TOAST */}
      {error && (
        <Box
          sx={{
            position: "fixed",
            bottom: 32,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 1200,
            background: "#FFFFFF",
            border: "1px solid #F7C1C1",
            borderRadius: "12px",
            padding: "14px 22px",
            display: "flex",
            alignItems: "center",
            gap: "10px",
            boxShadow: "0 8px 30px rgba(0,0,0,0.12)",
            minWidth: 220,
          }}
        >
          <ErrorOutlineIcon sx={{ color: "#E24B4A" }} />
          <div>
            <p style={{ fontSize: 12, fontWeight: 600, color: "#E24B4A", margin: 0 }}>Save failed</p>
            <p style={{ fontSize: 11, color: T.muted, margin: "2px 0 0" }}>{error}</p>
          </div>
        </Box>
      )}

      {/* heading */}
      <Box sx={{ mb: "28px" }}>
        <p style={{ fontSize: 9, color: T.muted, letterSpacing: "0.13em", textTransform: "uppercase", margin: "0 0 6px" }}>
          Property
        </p>
        <h2 style={{ fontSize: 20, fontWeight: 500, color: T.black, margin: 0 }}>Listing details</h2>
        <p style={{ fontSize: 12, color: T.muted, marginTop: 6 }}>
          Tell guests about your title, description, and neighborhood.
        </p>
      </Box>

      {/* FORM FIELDS — flat, no card wrap */}
      <Box sx={{ display: "flex", flexDirection: "column" }}>

        <TextField
          label="Title"
          placeholder="A cozy guesthouse near the lake"
          fullWidth
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          sx={muiFieldSx()}
          InputLabelProps={{ shrink: true }}
        />

        <TextField
          label="Description"
          placeholder="Tell guests about the space, the vibe, and anything else they should know..."
          multiline
          minRows={4}
          fullWidth
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          sx={muiFieldSx()}
          InputLabelProps={{ shrink: true }}
        />

        <TextField
          label="Neighborhood"
          placeholder="Describe what it's like around your place"
          fullWidth
          value={neighborhood}
          onChange={(e) => setNeighborhood(e.target.value)}
          sx={muiFieldSx()}
          InputLabelProps={{ shrink: true }}
        />
      </Box>

      {/* save */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
        <Button
          onClick={handleSave}
          disabled={isSaving}
          sx={{
            background: T.black,
            color: "#FFFFFF",
            border: "none",
            borderRadius: "10px",
            padding: "11px 28px",
            fontSize: 12,
            fontWeight: 600,
            fontFamily: T.sans,
            textTransform: "none",
            letterSpacing: "0.04em",
            boxShadow: "0 4px 14px rgba(0,0,0,0.18)",
            "&:hover": {
              background: T.black,
              boxShadow: "0 4px 14px rgba(0,0,0,0.18)",
            },
          }}
        >
          {isSaving ? "Saving..." : "Save changes"}
        </Button>
      </Box>
    </Box>
  );
};

export default ShortTitle;